import React, { useState, useCallback, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';
import { ModalService } from 'ui-components/services/modal-service';

import { BundleContext } from './bundle-context';
import { DoNotAutoSplit, SplitOnCustomQty, MinimumSplitOnCustomQty } from './constants/auto-split-options';
import {
  EnableProductPriceName,
  ProductPriceCurrencyCodeName,
  ProductPriceName,
  ShouldShowBundleOnPackingSlipName,
  ProductPackagePreference,
  ProductPackagingRequirements,
  ProductDangerousGoodsName,
  ShipStationFulfillmentBarcode,
  MaterialSafetyDataSheet,
  RequireSerialNumberName,
} from './constants/product-options';
import { locationNameFromParts } from './display-helpers';
import { mapMsdsProductOptionToMsdsFile, mapShipFromToProductWarehouse } from './mapping-helpers';
import { formattedMessageValidator, productIdValidator } from './prop-validator';
import { createProductDetailsSchema } from './validation/schema';
import { SAVE_DANGEROUS_GOODS_IN_PRODUCT } from '../../constants/analytics-events';
import { ProductsGridViewIds, InventoryGridViewIds } from '../../constants/grid-view-ids';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { DimensionUnit } from '../../enums/dimension-unit';
import { ProductTypes } from '../../enums/product-types';
import { WeightUnit } from '../../enums/weight-unit';
import { useEnableDangerousGoods } from '../../feature-flags/hooks/use-enable-dangerous-goods';
import { useEnableLtlFreight } from '../../feature-flags/hooks/use-enable-ltl-freight';
import { useEnableFulfillmentRates } from '../../feature-flags/hooks/use-enable-fulfillment-rates';
import { useIsSerialNumberTrackingAvailable } from '../../feature-gates/use-is-serial-number-tracking-available';
import { useShouldDisplayAutoRoutingControls } from '../../hooks/auto-routing/use-should-display-auto-routing-controls';
import { useSellerShipStationFulfillmentProvider } from '../../hooks/use-shipstation-fulfillment-whitelabel-enabled';
import { Segment } from '../../services/analytics-services/segment';
import { convertToDimensionUnitEnum } from '../../services/unit-services/dimensions/dimension-unit-enum-converter';
import { convertToWeightUnitEnum } from '../../services/unit-services/weights/weight-unit-enum-converter';
import Global from '../../store/global-selectors';
import {
  getInventoryWarehouses,
  getInventoryWarehouseLocations,
  getInventoryLocationsForProduct,
} from '../../store/inventory/inventory.api';
import { openProductDetailModal } from '../../store/products/products.actions';
import { getProductActiveShippingLocationsPromise } from '../../store/products/products.api';
import { Workflows } from '../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../tracking/use-workflow-tracking';
import { useYupFormValidation } from '../../validation/use-yup-form-validation';
import { ProductAliasPropType } from './components/tabs/aliases/prop-types/product-alias';
import { ProductLocationPropType } from './components/tabs/locations/prop-types/product-location';

const ProductDetailsContext = React.createContext(null);

const ProductDetailsContextProvider = ({
  userHasPermission = false,
  modalFrameworkMethods,
  modalProperties,
  productId,
  active = false,
  name = '',
  sku = '',
  productType: productTypeProp,
  productTypeText,
  headerText,
  fulfillmentSku = '',
  isReturnable: isReturnableProp = false,
  warehouseLocation = '',
  useProductName = false,
  serviceId,
  providerId,
  packageTypeId,
  confirmationId,
  internalNotes,
  intlServiceId,
  intlProviderId,
  intlPackageTypeId,
  intlConfirmationId,
  domesticRateShopperGuid: domesticRateShopperGuidProp,
  intlRateShopperGuid: intlRateShopperGuidProp,
  parentProduct = emptyObject,
  weight,
  weightUnit,
  convertedHeight,
  convertedWidth,
  convertedLength,
  sizeUnit,
  productProfiles = emptyArray,
  productProfileId,
  thumbnailUrl,
  tagIds,
  updateProduct,
  createProduct,
  upc: upcProp,
  noAutoCustomsDeclaration: noCustomsProp = false,
  customsCountry,
  customsDescription: customsDescProp,
  customsTariffNo,
  customsMIDCode: customsMIDCodeProp = '',
  cpscCertificateVersionId: cpscCertificateVersionIdProp = '',
  cpscCertifierId: cpscCertifierIdProp = '',
  customsValue,
  categoryId,
  children,
  modifiedDateUtc,
  createDateUtc,
  hasHadInventory = false,
  productAliases: productAliasesProp,
  shouldTrackInventory: shouldTrackProp = true,
  reorderThreshold: reorderProp = '',
  productVariantIds: productVariantIdsProp = emptyArray,
  autoSplitOption: autoSplitOptionProp = '',
  autoSplitCustomQuantity: autoSplitCustomQuantityProp = 2,
  productWarehouses: productWarehousesProp = emptyArray,
  productOptions = emptyArray,
  itemWeight: itemWeightProp = null,
  itemWeightUnitOfMeasureId: itemWeightUnitProp = null,
  itemLength: itemLengthProp = null,
  itemWidth: itemWidthProp = null,
  itemHeight: itemHeightProp = null,
  itemDimensionUnitOfMeasureId: itemDimensionUnitProp = null,
}) => {
  const { isBundle, getApiBundleComponents, bundlePackingSlipDisplayMode, hasExplicitBundlePackingSlipDisplayMode, isBundleValid } =
    useContext(BundleContext);
  const dispatch = useDispatch();
  const currentProfile = productProfiles.find((prof) => prof.productProfileId === productProfileId) ?? emptyObject;
  const [currentProductProfile, setCurrentProductProfile] = useState(currentProfile);

  const [isActive, setIsActive] = useState(!productId ? true : active);
  const [productName, setProductName] = useState(name);
  const [productSku, setProductSku] = useState(sku);
  const [shouldOverrideImportedName, setShouldOverrideImportedName] = useState(useProductName);
  const [productType, setProductType] = useState(productTypeProp || ProductTypes.Standalone.name);
  const [touchedFields, setTouchedFields] = useState(emptyArray);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  const [domesticServiceId, setDomesticServiceId] = useState(serviceId || '');
  const [domesticProviderId, setDomesticProviderId] = useState(providerId || '');
  const [domesticPackageTypeId, setDomesticPackageTypeId] = useState(packageTypeId || '');
  const [domesticConfirmationId, setDomesticConfirmationId] = useState(confirmationId || '');
  const [domesticRateShopperGuid, setDomesticRateShopperGuid] = useState(domesticRateShopperGuidProp || '');

  const [productCategoryId, setProductCategoryId] = useState(categoryId);
  const [description, setDescription] = useState(internalNotes || '');
  const [imageUrl, setImageUrl] = useState(thumbnailUrl || '');
  const [internationalServiceId, setInternationalServiceId] = useState(intlServiceId || '');
  const [internationalProviderId, setInternationalProviderId] = useState(intlProviderId || '');
  const [internationalPackageTypeId, setInternationalPackageTypeId] = useState(intlPackageTypeId || '');
  const [internationalConfirmationId, setInternationalConfirmationId] = useState(intlConfirmationId || '');
  const [internationalRateShopperGuid, setInternationalRateShopperGuid] = useState(intlRateShopperGuidProp || '');
  const [isReturnable, setIsReturnable] = useState(isReturnableProp);
  const [orderTagIds, setOrderTagIds] = useState(tagIds || []);

  const [weightValue, setWeightValue] = useState(weight);
  const [productItemWeight, setProductItemWeight] = useState({
    value: itemWeightProp ?? 0,
    unit: itemWeightUnitProp === WeightUnit.Unknown ? null : (itemWeightUnitProp ?? null),
  });
  const [productWarehouseLocation, setProductWarehouseLocation] = useState(warehouseLocation);
  const [dimensions, setDimensions] = useState({
    length: convertedLength,
    width: convertedWidth,
    height: convertedHeight,
  });
  const [productItemDimensions, setProductItemDimensions] = useState({
    length: itemLengthProp ?? 0,
    width: itemWidthProp ?? 0,
    height: itemHeightProp ?? 0,
    unit: itemDimensionUnitProp === DimensionUnit.Unknown ? null : (itemDimensionUnitProp ?? null),
  });

  const [productFulfillmentSku, setProductFulfillmentSku] = useState(fulfillmentSku);
  const [upc, setUpc] = useState(upcProp);
  const [fulfillmentBarcode, setFulfillmentBarcode] = useState(() => {
    const fulfillmentBarcodeOption = productOptions.find((option) => option.name === ShipStationFulfillmentBarcode);
    return fulfillmentBarcodeOption?.value;
  });

  const [noAutoCustomsDeclaration, setNoAutoCustoms] = useState(noCustomsProp);
  const [customsDescription, setCustomsDescription] = useState(customsDescProp);
  const [customsDeclaredValue, setCustomsDeclaredValue] = useState(customsValue);
  const [customsHarmonizationCode, setCustomsHarmonizationCode] = useState(customsTariffNo);
  const [customsMIDCode, setCustomsMIDCode] = useState(customsMIDCodeProp);
  const [cpscCertificateVersionId, setCpscCertificateVersionId] = useState(cpscCertificateVersionIdProp);
  const [cpscCertifierId, setCpscCertifierId] = useState(cpscCertifierIdProp);
  const [customsOriginCountry, setCustomsOriginCountry] = useState(customsCountry);

  const [inventoryWarehouses, setInventoryWarehouses] = useAsyncSafeState([]);
  const [inventoryWarehouseLocations, setInventoryWarehouseLocations] = useAsyncSafeState([]);

  const [shouldTrackInventory, setShouldTrackInventory] = useState(shouldTrackProp ?? true);
  const [reorderThreshold, setReorderThreshold] = useState(reorderProp);

  const [applyToOpenFulfillmentPlans, setApplyToOpenFulfillmentPlans] = useState(false);
  const [isLoadingInventoryLocations, setIsLoadingInventoryLocations] = useAsyncSafeState(false);
  const [shouldLoadInventoryLocations, setShouldLoadInventoryLocations] = useAsyncSafeState(true);
  const [inventoryLocations, setInventoryLocations] = useAsyncSafeState([]);

  const [isSaveInPlaceRequired, setIsSaveInPlaceRequired] = useState(false);
  const [isSaveInPlaceLoading, setIsSaveInPlaceLoading] = useState(false);

  const [productVariantIds, setProductVariantIds] = useState(productVariantIdsProp);
  const [autoSplitOption, setAutoSplitOption] = useState(autoSplitOptionProp);
  const [autoSplitCustomQuantity, setAutoSplitCustomQuantity] = useState(autoSplitCustomQuantityProp || MinimumSplitOnCustomQty);

  const [productPrice, setProductPrice] = useState(() => {
    const productPriceOption = productOptions.find((option) => option.name === ProductPriceName);
    const productPriceCurrencyCodeOption = productOptions.find((option) => option.name === ProductPriceCurrencyCodeName);

    return {
      value: productPriceOption ? Number(productPriceOption.value) : 0.0,
      code: productPriceCurrencyCodeOption ? productPriceCurrencyCodeOption.value : 'USD',
    };
  });

  const [enableProductPrice, setEnableProductPrice] = useState(() => {
    const enableProductPriceOption = productOptions.find((option) => option.name === EnableProductPriceName);

    return enableProductPriceOption ? enableProductPriceOption.value === 'true' : false;
  });

  const [packagePreference, setPackagePreference] = useState(
    () => productOptions.find((option) => option.name === ProductPackagePreference)?.value,
  );

  const [packagingRequirements, setPackagingRequirements] = useState(
    () => productOptions.find((option) => option.name === ProductPackagingRequirements)?.value,
  );

  const [requireSerialNumber, setRequireSerialNumber] = useState(
    () => productOptions.find((option) => option.name === RequireSerialNumberName)?.value === 'true',
  );

  // generate data from reference warehouses instead of every warehouse being on every product
  const sellerShipFroms = useSelector(Global.selectSellerShipFroms);
  const [productActiveLocations, setProductActiveLocations] = useState(() => {
    return sellerShipFroms.map((sf) => mapShipFromToProductWarehouse(sf, productWarehousesProp));
  });
  const [productDangerousGoods, setProductDangerousGoods] = useState(() => {
    const productDangerousGoodsOption = productOptions.find((option) => option.name === ProductDangerousGoodsName)?.value;

    if (!productDangerousGoodsOption) {
      return null;
    }

    try {
      const parsed = JSON.parse(productDangerousGoodsOption)[0];
      // Backfill meta flags for legacy DG data saved before the HAZMAT section existed.
      // Only applies when NEITHER flag is present — if hazmatSectionEnabled is already set,
      // this is new HAZMAT-only data and must not be overwritten.
      if (parsed && parsed.dgSectionEnabled === undefined && parsed.hazmatSectionEnabled === undefined) {
        parsed.dgSectionEnabled = true;
        parsed.hazmatSectionEnabled = false;
      }
      return parsed;
    } catch {
      return null;
    }
  });

  const [productMsdsFile, setProductMsdsFile] = useState(() => {
    const msdsProductOption = productOptions.find((option) => option.name === MaterialSafetyDataSheet);
    return mapMsdsProductOptionToMsdsFile(msdsProductOption);
  });

  const [harmonizationWorkflow, setHarmonizationWorkflow] = useState({
    isActive: false,
    selectedCode: null,
    selectedConfidence: null,
  });

  const sellerShipStationFulfillmentProvider = useSellerShipStationFulfillmentProvider();
  const isSmartRoutingEnabled = useShouldDisplayAutoRoutingControls();
  const fulfillmentRatesEnabled = useEnableFulfillmentRates();
  const dangerousGoodsEnabled = useEnableDangerousGoods();
  const hazmatEnabled = useEnableLtlFreight();
  const { isAvailable: isSerialNumberTrackingAvailable } = useIsSerialNumberTrackingAvailable();
  const trackHarmonization = useTrackWorkflow(Workflows.HARMONIZATION_SINGLE_FLOW);

  const { pathname } = useLocation();
  const { page, pageSize } = useParams();

  const isShipStationFulfillmentEnabled = !!sellerShipStationFulfillmentProvider;

  useEffect(() => {
    if (!productWarehousesProp.length) {
      getProductActiveShippingLocationsPromise().then((response) => {
        const data = response.data || [];
        setProductActiveLocations(sellerShipFroms.map((sf) => mapShipFromToProductWarehouse(sf, data)));
      });
    }
  }, [productWarehousesProp, sellerShipFroms]);

  const openDifferentProductModal = useCallback(
    (newProductId) => {
      const { modalId } = modalProperties;
      ModalService.closeModalById(modalId);
      dispatch(openProductDetailModal(newProductId));
    },
    [modalProperties, dispatch],
  );

  const loadInventoryLocations = useCallback(() => {
    setIsLoadingInventoryLocations(true);
    const inventoryPromise = getInventoryLocationsForProduct(productId);
    inventoryPromise.promise
      .then((response) => {
        const inventoryData = response.data || [];
        const locations = inventoryData.map((inv) => {
          const warehouse = inventoryWarehouses.find((wrh) => wrh.inventoryWarehouseId === inv.inventoryWarehouseId);
          return {
            ...inv,
            warehouseName: warehouse.name,
            location: locationNameFromParts(inv),
          };
        });
        setInventoryLocations(locations);
        setShouldLoadInventoryLocations(false);
        setIsLoadingInventoryLocations(false);
      })
      .catch(() => {
        setShouldLoadInventoryLocations(false);
        setIsLoadingInventoryLocations(false);
      });
    return () => inventoryPromise?.cancel();
  }, [productId, inventoryWarehouses, setInventoryLocations, setIsLoadingInventoryLocations, setShouldLoadInventoryLocations]);

  useEffect(() => {
    if (productId) {
      const warehousesPromise = getInventoryWarehouses();
      warehousesPromise.promise.then((response) => {
        const warehouseData = response.data || [];
        setInventoryWarehouses(warehouseData);
      });
      return () => {
        if (!warehousesPromise || !warehousesPromise.cancel) {
          return;
        }
        warehousesPromise?.cancel();
      };
    }
  }, [productId, setInventoryWarehouses]);

  useEffect(() => {
    const locationsPromise = getInventoryWarehouseLocations();
    locationsPromise.promise.then((response) => {
      const locationsData = response.data || [];
      const locationsWithNames = locationsData.map((loc) => ({
        ...loc,
        name: locationNameFromParts(loc),
      }));
      setInventoryWarehouseLocations(locationsWithNames);
    });
    return () => {
      if (!locationsPromise || !locationsPromise?.cancel) {
        return;
      }
      locationsPromise?.cancel();
    };
  }, [productId, setInventoryWarehouseLocations]);

  useEffect(() => {
    if (productId && inventoryWarehouses.length && shouldLoadInventoryLocations) {
      loadInventoryLocations();
    }
  }, [productId, inventoryWarehouses, shouldLoadInventoryLocations, loadInventoryLocations]);

  const [productAliases, setProductAliases] = useState(productAliasesProp || []);

  const generateProductOptions = useCallback(() => {
    const options = [];

    if (hasExplicitBundlePackingSlipDisplayMode) {
      options.push({
        name: ShouldShowBundleOnPackingSlipName,
        value: bundlePackingSlipDisplayMode,
      });
    }
    if (fulfillmentRatesEnabled) {
      options.push({ name: ProductPriceName, value: productPrice.value });
      options.push({
        name: ProductPriceCurrencyCodeName,
        value: productPrice.code,
      });
      options.push({ name: EnableProductPriceName, value: enableProductPrice });
    }
    options.push({ name: ProductPackagePreference, value: packagePreference });
    options.push({ name: ProductPackagingRequirements, value: packagingRequirements });
    if (isSerialNumberTrackingAvailable) {
      options.push({ name: RequireSerialNumberName, value: requireSerialNumber ? 'true' : 'false' });
    }
    if (dangerousGoodsEnabled || hazmatEnabled) {
      // In the basic assumption, a product can have many dangerous goods,
      // but at the beginning we want to allow the user to assign only a single dangerous good.
      // If in the future we allow the user to create many dangerous goods for a product, the model is already ready.
      if (productDangerousGoods) {
        const value = JSON.stringify([productDangerousGoods]);
        options.push({ name: ProductDangerousGoodsName, value });

        const originalDgValue = productOptions.find((option) => option.name === ProductDangerousGoodsName)?.value;
        // track only when dangerous goods are added - not updated
        const shouldTrack = !originalDgValue;
        if (shouldTrack) {
          Segment.trackEvent(SAVE_DANGEROUS_GOODS_IN_PRODUCT, {
            value: [productDangerousGoods],
          });
        }
      }
    }
    if (isShipStationFulfillmentEnabled) {
      options.push({
        name: ShipStationFulfillmentBarcode,
        value: fulfillmentBarcode,
      });
    }
    return options;
  }, [
    isSerialNumberTrackingAvailable,
    requireSerialNumber,
    dangerousGoodsEnabled,
    hazmatEnabled,
    enableProductPrice,
    fulfillmentRatesEnabled,
    packagePreference,
    packagingRequirements,
    productDangerousGoods,
    productOptions,
    productPrice.code,
    productPrice.value,
    bundlePackingSlipDisplayMode,
    hasExplicitBundlePackingSlipDisplayMode,
    fulfillmentBarcode,
    isShipStationFulfillmentEnabled,
  ]);

  // Build validation form object
  const validationForm = useMemo(
    () => ({
      productName,
      productSku,
      upc,
      warehouseLocation: productWarehouseLocation,
      customsDeclaredValue,
      customsMIDCode,
    }),
    [productName, productSku, upc, productWarehouseLocation, customsDeclaredValue, customsMIDCode],
  );

  // Create schema based on whether this is a new product
  const isNewProduct = !productId;
  const productDetailsSchema = useMemo(() => createProductDetailsSchema(isNewProduct), [isNewProduct]);

  // Use validation hook
  const { errors: validationErrors, isValid: isFormValid } = useYupFormValidation({
    form: validationForm,
    schema: productDetailsSchema,
  });

  // Helper to mark a field as touched
  const markFieldTouched = useCallback((fieldName) => {
    setTouchedFields((prev) => (prev.includes(fieldName) ? prev : [...prev, fieldName]));
  }, []);

  const onSaveClicked = useCallback(
    (reuseNewProductModal = false) => {
      // Mark that save was attempted (this will show all validation errors)
      setHasAttemptedSave(true);

      // Bundle validation check
      if (!isBundleValid()) {
        return;
      }

      // Yup validation check (includes Name, SKU, and other fields)
      if (!isFormValid) {
        return; // Don't save, but errors are now visible to user
      }
      const sanitizedForm = {
        productId,
        productProfileId: currentProductProfile.productProfileId || null,
        isActive,
        name: productName,
        sku: productSku,
        productType: productType || ProductTypes.Standalone.name,
        categoryId: productCategoryId || null,
        useProductName: shouldOverrideImportedName || null,
        serviceId: domesticServiceId || null,
        providerId: domesticProviderId || null,
        packageTypeId: domesticPackageTypeId || null,
        confirmationId: domesticConfirmationId || null,
        domesticRateShopperGuid: domesticRateShopperGuid || null,
        internalNotes: description || null,
        intlServiceId: internationalServiceId || null,
        intlProviderId: internationalProviderId || null,
        intlPackageTypeId: internationalPackageTypeId || null,
        intlConfirmationId: internationalConfirmationId || null,
        intlRateShopperGuid: internationalRateShopperGuid || null,
        isReturnable,
        weight: weightValue || null,
        weightUnitOfMeasureId: weightValue ? convertToWeightUnitEnum(weightUnit) : null,
        length: dimensions.length || null,
        width: dimensions.width || null,
        height: dimensions.height || null,
        dimensionUnitOfMeasureId: convertToDimensionUnitEnum(sizeUnit),
        itemWeight: productItemWeight.value ?? null,
        itemWeightUnitOfMeasureId:
          productItemWeight.value && productItemWeight.unit ? convertToWeightUnitEnum(productItemWeight.unit) : null,
        itemLength: productItemDimensions.length ?? null,
        itemWidth: productItemDimensions.width ?? null,
        itemHeight: productItemDimensions.height ?? null,
        itemDimensionUnitOfMeasureId: productItemDimensions.unit ? convertToDimensionUnitEnum(productItemDimensions.unit) : null,
        thumbnailUrl: imageUrl || null,
        tagIds: orderTagIds || null,
        upc: upc || null,
        shouldAutoCreateCustomsDeclaration: !noAutoCustomsDeclaration,
        customsCountry: customsOriginCountry,
        customsDescription,
        customsTariffNo: customsHarmonizationCode,
        customsMIDCode,
        cpscCertificateVersionId,
        cpscCertifierId,
        customsValue: customsDeclaredValue,
        updateProduct,
        createProduct,
        fulfillmentSku: productFulfillmentSku || null,
        warehouseLocation: productWarehouseLocation || null,
        productAliases: productAliases.map((alias) => ({
          ...alias,
          productAliasId: alias.isNewAlias ? undefined : alias.productAliasId,
        })),
        shouldTrackInventory,
        reorderThreshold,
        autoSplitOption: autoSplitOption || DoNotAutoSplit,
        autoSplitCustomQuantity: autoSplitOption === SplitOnCustomQty ? autoSplitCustomQuantity : null,
        productActiveLocations,
        bundleComponents: getApiBundleComponents(),
        isBundle,
        productOptions: generateProductOptions(),
      };

      const onSaveSuccess = () => {
        if (harmonizationWorkflow.isActive && harmonizationWorkflow.selectedCode) {
          trackHarmonization(WorkflowSteps.COMPLETE, {
            product: {
              productId,
              code: harmonizationWorkflow.selectedCode,
              confidenceLevel: harmonizationWorkflow.selectedConfidence,
            },
          });
        }
      };

      const updateOptions = {
        applyToOpenFulfillmentPlans,
        reuseNewProductModal,
        shouldReloadProductsGrid: active !== isActive,
        shouldReloadInventoryGrid:
          pathname === InventoryGridViewIds.PATHNAME ? InventoryGridViewIds.INVENTORY : ProductsGridViewIds.PRODUCTS,
        pageData: {
          page,
          pageSize,
          searchTerm: productSku,
        },
        onSuccessCallback: onSaveSuccess,
      };

      const { modalId } = modalProperties;
      if (!productId) {
        createProduct(sanitizedForm, updateOptions, modalId);
      } else {
        updateProduct(sanitizedForm, updateOptions, modalId);
      }
    },
    [
      productId,
      productSku,
      isBundleValid,
      isFormValid,
      currentProductProfile,
      isActive,
      productName,
      productType,
      productCategoryId,
      shouldOverrideImportedName,
      domesticServiceId,
      domesticProviderId,
      domesticPackageTypeId,
      domesticConfirmationId,
      domesticRateShopperGuid,
      description,
      internationalServiceId,
      internationalProviderId,
      internationalPackageTypeId,
      internationalConfirmationId,
      internationalRateShopperGuid,
      isReturnable,
      weightValue,
      weightUnit,
      dimensions,
      sizeUnit,
      productItemWeight,
      productItemDimensions,
      imageUrl,
      orderTagIds,
      upc,
      noAutoCustomsDeclaration,
      customsOriginCountry,
      customsDescription,
      customsHarmonizationCode,
      customsMIDCode,
      cpscCertificateVersionId,
      cpscCertifierId,
      customsDeclaredValue,
      updateProduct,
      createProduct,
      productFulfillmentSku,
      productWarehouseLocation,
      productAliases,
      shouldTrackInventory,
      reorderThreshold,
      autoSplitOption,
      autoSplitCustomQuantity,
      productActiveLocations,
      getApiBundleComponents,
      isBundle,
      generateProductOptions,
      applyToOpenFulfillmentPlans,
      active,
      pathname,
      page,
      pageSize,
      modalProperties,
      harmonizationWorkflow,
      trackHarmonization,
    ],
  );

  // wrapped setter functions are wrapped to avoid a console error thrown
  // by react, setters for useState only accept 1 arg
  const memoizedSetShouldOverrideImportedName = useCallback(
    (isChecked) => setShouldOverrideImportedName(isChecked),
    [setShouldOverrideImportedName],
  );
  const memoizedSetWeightValue = useCallback((value) => setWeightValue(value), [setWeightValue]);
  const memoizedSetApplyToOpenFulfillmentPlans = useCallback(
    (isChecked) => setApplyToOpenFulfillmentPlans(isChecked),
    [setApplyToOpenFulfillmentPlans],
  );

  const [selectedTab, setSelectedTab] = useState();
  const [activity, setActivity] = useState(emptyObject);

  const closeModal = useCallback(() => {
    if (harmonizationWorkflow.isActive) {
      trackHarmonization(WorkflowSteps.CANCEL, {
        selectedProduct: harmonizationWorkflow.selectedCode
          ? {
              productId,
              code: harmonizationWorkflow.selectedCode,
            }
          : {},
      });
    }

    modalFrameworkMethods.closeModal();
  }, [modalFrameworkMethods, harmonizationWorkflow, trackHarmonization, productId]);

  const productDetailsContextValue = {
    productId,
    userHasPermission,
    closeModal,
    headerText,
    isActive,
    setIsActive: (isChecked) => setIsActive(isChecked),
    isVariant: productType === ProductTypes.Variant.name,
    currentProductProfile,
    setCurrentProductProfile,
    productProfiles,
    productName,
    setProductName,
    productSku,
    setProductSku,
    shouldOverrideImportedName,
    setShouldOverrideImportedName: memoizedSetShouldOverrideImportedName,
    productTypeText,
    weightValue,
    productItemWeight,
    setProductItemWeight,
    setWeightValue: memoizedSetWeightValue,
    weightUnit,
    productWarehouseLocation,
    setProductWarehouseLocation,
    description,
    setDescription,
    dimensions,
    setDimensions,
    productItemDimensions,
    setProductItemDimensions,
    sizeUnit,
    productFulfillmentSku,
    setProductFulfillmentSku,
    domesticServiceId,
    setDomesticServiceId,
    domesticProviderId,
    setDomesticProviderId,
    domesticPackageTypeId,
    setDomesticPackageTypeId,
    domesticConfirmationId,
    setDomesticConfirmationId,
    domesticRateShopperGuid,
    setDomesticRateShopperGuid,
    orderTagIds,
    setOrderTagIds,
    internationalServiceId,
    setInternationalServiceId,
    internationalProviderId,
    setInternationalProviderId,
    internationalPackageTypeId,
    setInternationalPackageTypeId,
    internationalConfirmationId,
    setInternationalConfirmationId,
    internationalRateShopperGuid,
    setInternationalRateShopperGuid,
    isReturnable,
    setIsReturnable,
    productCategoryId,
    setProductCategoryId,
    applyToOpenFulfillmentPlans,
    setApplyToOpenFulfillmentPlans: memoizedSetApplyToOpenFulfillmentPlans,
    parentProduct,
    imageUrl,
    setImageUrl,
    onSaveClicked,
    selectedTab,
    setSelectedTab,
    upc,
    setUpc,
    fulfillmentBarcode,
    setFulfillmentBarcode,
    modifiedDateUtc,
    createDateUtc,
    hasHadInventory,
    noAutoCustomsDeclaration,
    setNoAutoCustoms,
    customsDescription,
    setCustomsDescription,
    customsDeclaredValue,
    setCustomsDeclaredValue,
    customsHarmonizationCode,
    setCustomsHarmonizationCode,
    customsMIDCode,
    setCustomsMIDCode,
    cpscCertificateVersionId,
    setCpscCertificateVersionId,
    cpscCertifierId,
    setCpscCertifierId,
    customsOriginCountry,
    setCustomsOriginCountry,
    activity,
    setActivity,
    productAliases,
    setProductAliases,
    inventoryWarehouses,
    inventoryWarehouseLocations,
    shouldTrackInventory,
    setShouldTrackInventory,
    reorderThreshold,
    setReorderThreshold,
    inventoryLocations,
    isLoadingInventoryLocations,
    setShouldLoadInventoryLocations,
    productVariantIds,
    setProductVariantIds,
    productType,
    setProductType,
    isSaveInPlaceRequired,
    setIsSaveInPlaceRequired,
    isSaveInPlaceLoading,
    setIsSaveInPlaceLoading,
    openDifferentProductModal,
    autoSplitOption,
    setAutoSplitOption,
    autoSplitCustomQuantity,
    setAutoSplitCustomQuantity,
    productActiveLocations,
    setProductActiveLocations,
    isSmartRoutingEnabled,
    bundlePackingSlipDisplayMode,
    productPrice,
    setProductPrice,
    enableProductPrice,
    setEnableProductPrice,
    packagePreference,
    setPackagePreference,
    packagingRequirements,
    setPackagingRequirements,
    requireSerialNumber,
    setRequireSerialNumber,
    productDangerousGoods,
    setProductDangerousGoods,
    productMsdsFile,
    setProductMsdsFile,
    isShipStationFulfillmentEnabled,
    validationErrors,
    isFormValid,
    markFieldTouched,
    touchedFields,
    hasAttemptedSave,
    harmonizationWorkflow,
    setHarmonizationWorkflow,
  };

  return <ProductDetailsContext.Provider value={productDetailsContextValue}>{children}</ProductDetailsContext.Provider>;
};

ProductDetailsContextProvider.propTypes = {
  userHasPermission: PropTypes.bool,
  productId: productIdValidator.isRequired,
  active: PropTypes.bool,
  sku: PropTypes.string,
  name: PropTypes.string,
  fulfillmentSku: PropTypes.string,
  warehouseLocation: PropTypes.string,
  useProductName: PropTypes.bool,
  productType: PropTypes.string.isRequired,
  productTypeText: formattedMessageValidator.isRequired,
  headerText: formattedMessageValidator.isRequired,
  serviceId: PropTypes.string,
  providerId: PropTypes.string,
  packageTypeId: PropTypes.string,
  confirmationId: PropTypes.string,
  internalNotes: PropTypes.string,
  intlServiceId: PropTypes.string,
  intlProviderId: PropTypes.string,
  intlPackageTypeId: PropTypes.string,
  intlConfirmationId: PropTypes.string,
  domesticRateShopperGuid: PropTypes.string,
  intlRateShopperGuid: PropTypes.string,
  isReturnable: PropTypes.bool,
  parentProduct: PropTypes.object,
  weight: PropTypes.number,
  weightUnit: PropTypes.string,
  convertedHeight: PropTypes.number,
  convertedWidth: PropTypes.number,
  convertedLength: PropTypes.number,
  sizeUnit: PropTypes.string,
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
  modalProperties: PropTypes.object.isRequired,
  productProfiles: PropTypes.array,
  productProfileId: PropTypes.string,
  thumbnailUrl: PropTypes.string,
  tagIds: PropTypes.arrayOf(PropTypes.string),
  upc: PropTypes.string,
  noAutoCustomsDeclaration: PropTypes.bool,
  customsCountry: PropTypes.string,
  customsDescription: PropTypes.string,
  customsTariffNo: PropTypes.string,
  customsMIDCode: PropTypes.string,
  cpscCertificateVersionId: PropTypes.string,
  cpscCertifierId: PropTypes.string,
  customsValue: PropTypes.string,
  updateProduct: PropTypes.func.isRequired,
  createProduct: PropTypes.func.isRequired,
  categoryId: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  modifiedDateUtc: PropTypes.string,
  createDateUtc: PropTypes.string,
  hasHadInventory: PropTypes.bool,
  productAliases: PropTypes.arrayOf(ProductAliasPropType).isRequired,
  shouldTrackInventory: PropTypes.bool,
  reorderThreshold: PropTypes.string,
  productVariantIds: PropTypes.arrayOf(PropTypes.string),
  autoSplitOption: PropTypes.string,
  autoSplitCustomQuantity: PropTypes.number,
  productWarehouses: PropTypes.arrayOf(ProductLocationPropType),
  productOptions: PropTypes.array,
};

export { ProductDetailsContext, ProductDetailsContextProvider };
